import { isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { uiActions } from '../ui/slice';
import { outputActions } from './slice';
import { startAppListening } from '../../listener';
import { Action, BROADCAST_CHANNEL } from './constants';
import { RootState, StoreType } from '../../store';
import { currentThemeSelector } from '../ui/selectors';
import { WindowSize } from '../settings/types';
import { settingsActions } from '../settings/slice';
import { themeActions } from '../themes/slice';
import { NewTheme, Theme } from '../themes/types';
import { rendererActions } from '../renderer/slice';
import { CacheRenderedResourceAction } from '../renderer/types';
import { authActions } from '../auth/slice';
import { SetResourcePayload } from '../ui/types';

export const startOutputBroadcast = (store: StoreType) => {

  const broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL);

  const sendCurrentTheme = (state: RootState, override?: Theme | undefined) => {
    const currentTheme = override || currentThemeSelector(state);
    if (currentTheme) {
      broadcastChannel.postMessage(
        uiActions.setCurrentTheme(currentTheme)
      );
    }
  };

  const sendCurrentLiveResource = (state: RootState) => {
    const currentLiveResource = state.ui.currentLiveResource;
    const themeCaches = state.renderCache;
    const globalThemeID = state.ui.currentThemeID;
    const ccliNumber = state.settings.ccli.licenseNumber;
    const themeCache = themeCaches[currentLiveResource?.annotations?.themeID as string || globalThemeID!];
    if (currentLiveResource && themeCache) {
      broadcastChannel.postMessage(uiActions.setCurrentLiveResource({
        resource: themeCache[currentLiveResource.id],
        blockAddress: state.output.address,
        annotations: {
          ccliNumber
        }
      }));
    }
    else {
      broadcastChannel.postMessage(uiActions.setCurrentLiveResource(null));
    }
  };

  const sendLiveAddress = (state: RootState) => {
    const currentAddress = state.output.address;
    broadcastChannel.postMessage(outputActions.setBlockAddress(currentAddress));
  };

  const sendCanvasSize = (state: RootState) => {
    const canvasSize: WindowSize = state.settings.display;
    broadcastChannel.postMessage({
      action: Action.CANVAS_RESIZED,
      canvasSize
    });
  };

  const unsubscribers = [
    startAppListening({
      matcher: isAnyOf(
        outputActions.setBlanked,
        outputActions.setBlockAddress,
        outputActions.setFadeToBlack,
      ),
      effect: (action) => {
        broadcastChannel.postMessage(action);
      }
    }),
    startAppListening({
      matcher: isAnyOf(
        uiActions.setCurrentLiveResource,
        uiActions.setCurrentThemeID
      ),
      effect: (action, api) => {
        const state = api.getState();
        let overrideTheme = undefined;
        if (action.type === uiActions.setCurrentLiveResource.type) {
          const maybeOverriddenThemeID = (action.payload as SetResourcePayload).annotations?.themeID;
          if (maybeOverriddenThemeID) {
            overrideTheme = state.themes[maybeOverriddenThemeID as string];
          }
        }
        sendCurrentTheme(state, overrideTheme);
        sendCurrentLiveResource(state);
      }
    }),
    startAppListening({
      matcher: isAnyOf(
        settingsActions.setCanvasSize
      ),
      effect: (_, api) => {
        const state = api.getState();
        sendCanvasSize(state);
      }
    }),
    startAppListening({
      matcher: isAnyOf(
        themeActions.saveTheme
      ),
      effect: (action: PayloadAction<NewTheme>, api) => {
        const state = api.getState();
        if (state.ui.currentThemeID === action.payload.id) {
          sendCurrentTheme(state);
        }
      }
    }),
    startAppListening({
      matcher: isAnyOf(
        rendererActions.cacheRenderedResource
      ),
      effect: (action: PayloadAction<CacheRenderedResourceAction>, api) => {
        const state = api.getState();
        if (state.ui.currentLiveResource?.id === action.payload.resource.id) {
          sendCurrentLiveResource(state);
        }
      }
    }),
    startAppListening({
      matcher: isAnyOf(
        authActions.logOut,
        settingsActions.setOutputWindows
      ),
      effect: () => {
        broadcastChannel.postMessage({ action: Action.CLOSE });
      }
    }),
  ];

  broadcastChannel.addEventListener('message', (msg) => {
    if (msg.data?.action === Action.WINDOW_OPENED) {
      const state: RootState = store.getState();
      sendCanvasSize(state);
      sendCurrentTheme(state);
      sendCurrentLiveResource(state);
      sendLiveAddress(state);
    }
  });

  return () => {
    for (const unsub of unsubscribers) {
      unsub();
    }
    broadcastChannel.postMessage({ action: Action.CLOSE });
    broadcastChannel.close();
  };
};
